import * as React from "react"
import { Link } from "react-router-dom"
import { AlertCircle } from "lucide-react"

import { LineaDeMargen, Logotype } from "@/components/layout/Logotype"
import { cn } from "@/lib/utils"

/**
 * EL SOPORTE DE LAS DOS PORTADAS.
 *
 * Entrar y darse de alta son **dos piezas y dos páginas**, y las dos se montan
 * aquí para que sean la misma pieza con distinto contenido. Cesar: «deberías
 * tener dos páginas, registro y log in, que cumplen roles diferentes. En ambas
 * mejora y vuelve más minimalista».
 *
 * ===========================================================================
 * LA LÍNEA VA AL BORDE, Y EL CONTENIDO AL CENTRO
 * ===========================================================================
 *
 * «Sí o sí la línea roja, si la pones, debe ir a la izquierda; pero sí
 * centraría, o lo haría más bonito y más minimalista, sin tanto floro.»
 *
 * La versión anterior tenía las dos cosas peleadas: la línea a 1,25rem del
 * borde y **el contenido alineado contra ella** con `pl-8`. Eso obliga a que
 * todo —identificador, titular, párrafo, botón— cuelgue del margen izquierdo,
 * que es una composición editorial, no una portada de entrada. Y deja la mitad
 * derecha de la pantalla vacía sin que nada la justifique.
 *
 * Ahora son dos ejes independientes y cada uno hace su trabajo:
 *
 *   LA LÍNEA es del soporte y se va al **borde mismo** de la pantalla, de
 *   arriba abajo. Ahí es el renglón del libro rayado —el margen de la hoja— y
 *   no el eje de un bloque de texto. Deja de tirar de la composición.
 *
 *   EL CONTENIDO se centra, en una columna estrecha. Sin nada que lo empuje
 *   hacia un lado, la pieza se lee de un golpe: identificador, qué es esta
 *   página, y el botón.
 *
 * ---------------------------------------------------------------------------
 * EL ACENTO ROJO ESTÁ GASTADO, Y POR ESO NO HAY MÁS
 *
 * DESIGN.md §2, literal: «una pantalla con la línea de margen de arriba abajo
 * ya no puede tener otro filete rojo». Así que en estas dos páginas **no va la
 * nota de Marty** —fondo rayado con filete rojo a la izquierda— ni el punto del
 * titular. El único rojo que acompaña a la línea es el punto del identificador,
 * que es parte de la marca y no un acento de la pieza.
 *
 * ---------------------------------------------------------------------------
 * LO QUE SE QUITÓ, QUE ES «EL FLORO»
 *
 * La portada tenía cuatro bloques de texto antes del primer botón: el titular,
 * un párrafo de tres líneas sobre lo que hace el producto, la presentación de
 * Marty en primera persona y el descargo sobre la firma colegiada. Ninguno se
 * lee: quien llega a esta pantalla viene a entrar, y ya sabe qué es esto.
 *
 * El descargo **no se borra del producto**: se queda en el registro, que es
 * donde alguien decide empezar, y ahí sí es información y no decoración.
 */
export function Portada({
  titulo,
  bajada,
  error,
  children,
  pie,
}: {
  /** Qué es esta página. Es el `<h1>`, y va sin punto: ver el acento rojo. */
  titulo: string
  /** Una línea, o ninguna. Si necesita dos, sobra una. */
  bajada?: string
  error?: string | null
  children: React.ReactNode
  /** El cruce a la otra página, y lo que haga falta decir al final. */
  pie?: React.ReactNode
}) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/*
        EL RAYADO DEL LIBRO MAYOR, DESVANECIDO EN EL CENTRO.

        DESIGN.md §2 pide dos cosas del rayado que en una composición CENTRADA se
        contradicen: que sea la textura del soporte, y que «no se acerque al
        identificador». Mientras la pieza era una columna pegada al margen
        izquierdo se cumplían las dos; con el contenido en mitad de la pantalla,
        los renglones pasan por encima de la M y por detrás de los campos. En la
        primera captura de esta versión se veía: la palabra «Marty» tachada por
        tres líneas.

        Borrar el rayado dejaba la página igual a cualquier otro login. Así que
        se enmascara: **ausente donde vive el contenido y presente hacia los
        bordes**, que es donde es papel rayado y no ruido. La máscara es una
        elipse, no un rectángulo, para que no se vea el canto por donde los
        renglones se cortan.

        `WebkitMaskImage` va también: Safari todavía lo pide con prefijo, y sin
        él la máscara no se aplica — el rayado volvería entero, encima de la M.
      */}
      <div
        aria-hidden="true"
        className="rayado pointer-events-none absolute inset-0"
        style={{
          maskImage:
            "radial-gradient(ellipse 34rem 34rem at center, transparent 40%, black 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 34rem 34rem at center, transparent 40%, black 100%)",
        }}
      />

      {/*
        LA LÍNEA, EN EL BORDE. `left="0"` y no «a 1,25rem del borde»: pegada al
        canto es el margen de la hoja; a dos centímetros era el eje de un texto
        que ya no está alineado con ella. `inset-y-0` sobre un contenedor con
        `min-h-screen` la hace atravesar el soporte entero (lámina 09).
      */}
      <LineaDeMargen left="0" grosor="2px" />

      <div className="relative flex min-h-screen items-center justify-center px-6 py-16">
        {/*
          LA COLUMNA. 20rem: el ancho de un formulario de una sola columna sin
          que los campos se estiren hasta parecer una tabla. Todo lo de dentro
          comparte este eje, y el eje está en el centro de la pantalla.
        */}
        <div className="w-full max-w-[20rem]">
          {/*
            EL IDENTIFICADOR, centrado y grande. `compacto` —sin línea propia—
            porque la línea ya la dibuja el soporte, y dos líneas de margen en
            una misma pieza es lo que había antes de esta versión.
          */}
          <div className="flex justify-center">
            <Logotype variante="compacto" tamano="2xl" className="h-auto" />
          </div>

          <h1 className="titular mt-8 text-center text-lg text-foreground">{titulo}</h1>
          {bajada ? (
            <p className="mx-auto mt-1.5 max-w-[30ch] text-center text-2xs leading-4 text-muted-foreground">
              {bajada}
            </p>
          ) : null}

          {/*
            EL ERROR, ENTRE EL TÍTULO Y EL FORMULARIO. Debajo del botón obliga a
            volver a subir la vista para corregir el campo que falló; aquí se lee
            en el camino. Con icono y texto, nunca sólo color (brandbook).
          */}
          {error ? (
            <p
              role="alert"
              className="mt-6 flex items-start gap-1.5 rounded-md border border-destructive/35 bg-destructive-soft p-2.5 text-2xs leading-4 text-destructive"
            >
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {error}
            </p>
          ) : null}

          <div className="mt-7">{children}</div>

          {pie ? <div className="mt-7">{pie}</div> : null}
        </div>
      </div>
    </div>
  )
}

/**
 * EL SEPARADOR ENTRE EL PROVEEDOR Y EL CORREO.
 *
 * Un filete a cada lado de la palabra. Va en el color de borde y no en rojo: el
 * acento de la pieza ya lo gasta la línea de margen.
 */
export function Separador({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-5 flex items-center gap-3" aria-hidden="true">
      <span className="h-px flex-1 bg-border" />
      <span className="text-2xs uppercase tracking-[0.08em] text-muted-foreground">
        {children}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

/** El cruce a la otra portada. Una frase, con el verbo enlazado. */
export function CruceDePortada({
  pregunta,
  accion,
  a,
}: {
  pregunta: string
  accion: string
  a: string
}) {
  return (
    <p className="text-center text-2xs text-muted-foreground">
      {pregunta}{" "}
      <Link
        to={a}
        className={cn(
          "rounded-sm font-medium text-brand underline underline-offset-2",
          "transition-colors hover:text-brand/80",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        {accion}
      </Link>
    </p>
  )
}

/**
 * LA MARCA DE GOOGLE, en sus cuatro colores y puestos a mano.
 *
 * No usa `currentColor` a propósito: las directrices de «Sign in with Google»
 * exigen la G de cuatro colores o la blanca sobre azul, y una G monocroma
 * heredando el color del botón no es ninguna de las dos. Es además lo que la
 * hace reconocible sin leer el texto, que es el punto de tener logotipo en un
 * botón de proveedor.
 */
export function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className={className}>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571h.003l6.19 5.238C37.771 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  )
}

/**
 * Los errores de Firebase, en español y diciendo qué hacer.
 *
 * Vive aquí y no en cada página porque las dos hablan con el mismo proveedor y
 * un mensaje distinto para el mismo código es un mensaje que se contradice.
 */
export function traducirError(err: unknown) {
  const code = (err as { code?: string })?.code ?? ""
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Ese correo y esa contraseña no coinciden."
    case "auth/operation-not-allowed":
      /*
        Este es el que sale de verdad al usar el formulario: el proyecto tiene el
        ingreso por contraseña deshabilitado. Antes caía en `default` y devolvía
        el mensaje crudo de Firebase en inglés, que no dice qué hacer.
      */
      return "El ingreso por contraseña está deshabilitado en este proyecto. Entra con Google."
    case "auth/email-already-in-use":
      return "Ya hay una cuenta con ese correo. Entra en vez de crearla."
    case "auth/weak-password":
      return "La contraseña es muy corta. Mínimo seis caracteres."
    case "auth/invalid-email":
      return "Ese correo no tiene forma de correo."
    case "auth/too-many-requests":
      return "Demasiados intentos. Espera unos minutos antes de volver a probar."
    case "auth/popup-closed-by-user":
      return "Se cerró la ventana de Google antes de terminar."
    case "auth/popup-blocked":
      return "El navegador bloqueó la ventana de Google. Permite las ventanas emergentes de este sitio y vuelve a intentarlo."
    case "auth/network-request-failed":
      return "No llegué al servidor de identidad. Revisa tu conexión."
    default:
      return err instanceof Error ? err.message : "No pude iniciar la sesión."
  }
}
